import React, { useState, useRef } from 'react';
import TabsComponent from '@components/tabs/tabs.tsx';
import {
  mediaLibraryDialogTabsData,
  mediaLibraryErrorMessages
} from '@utils/media-library-utils.ts';
import { AddMediaIcon, ArrowLeftIcon } from '@components/icons/svg-icon.tsx';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import { useForm } from 'react-hook-form';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';

const AddMediaContent: React.FC<{
  handleClose: () => void;
}> = ({ handleClose }) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { uploadFileOrFiles, setSelectedFileFromUrl, isUploadByUrlLoading } =
    useMediaLibraryStore();
  const { showSnackbar } = useSnackbarStore();

  const {
    register,
    formState: { errors },
    handleSubmit
  } = useForm<any>({ mode: 'onTouched' });

  const [currentTab, setCurrentTab] = useState<string>(
    mediaLibraryDialogTabsData[0].value
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleChangeTab = (_e: React.SyntheticEvent, value: string) => {
    setCurrentTab(value);
  };

  const handleOpenFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length === 0) return;

    const MAX_SIZE_MB = 200;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    const validFiles: File[] = [];
    const oversizedFiles: string[] = [];

    files.forEach((file) => {
      if (file.size > MAX_SIZE_BYTES) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (oversizedFiles.length > 0) {
      showSnackbar(
        `File${oversizedFiles.length > 1 ? 's' : ''} too large: ${oversizedFiles.join(
          ', '
        )} (max ${MAX_SIZE_MB}MB)`,
        SnackbarType.Error
      );
    }

    if (validFiles.length > 0) {
      uploadFileOrFiles({ data: validFiles, handleClose });
    }

    event.target.value = '';
  };

  return (
    <>
      <TabsComponent
        tabs={mediaLibraryDialogTabsData}
        value={currentTab}
        onChange={handleChangeTab}
      />

      <div className="mt-8">
        {currentTab === 'from-computer' && (
          <div>
            <input
              type="file"
              hidden
              multiple
              ref={fileInputRef}
              onChange={handleSelectFile}
              accept="
                image/*,
                video/*,
                audio/*,
                text/html,
                .htm,
                .html,
                .apk,
                .ipk,
                .wgt,
                .m3u8,
                .m3u
              "
            />

            <div
              onClick={handleOpenFileDialog}
              className="min-h-150 flex justify-center items-center bg-(--gray-11) rounded-[20px] border border-solid border-(--gray-7) cursor-pointer max-lg-xl:min-h-100"
            >
              <div className="flex flex-col items-center gap-5">
                <div>
                  <AddMediaIcon className="w-50! h-50! max-lg-xl:w-30! max-lg-xl:h-30!" />
                </div>

                <p className="text-(--blue-4) text-[30px] font-semibold opacity-40 max-lg-xl:text-[20px]">
                  {t('choose_your_media')}
                </p>
              </div>
            </div>

            <div className="mt-7">
              <Button
                variant="outlined"
                startIcon={<ArrowLeftIcon />}
                onClick={handleClose}
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        )}

        {currentTab === 'from-url' && (
          <form
            onSubmit={handleSubmit((data) =>
              setSelectedFileFromUrl(data.url, handleClose)
            )}
          >
            <div className="w-full min-h-150 max-lg-xl:min-h-100">
              <FormControl className="w-full" error={!!errors.url}>
                <FormLabel
                  className="flex"
                  sx={{ color: 'var(--blue-4)', fontWeight: 500 }}
                >
                  {t('label_url')}
                </FormLabel>
                <OutlinedInput
                  multiline
                  rows={12}
                  {...register('url', {
                    required: true,
                    pattern:
                      /^(https?:\/\/)([a-z\d.-]+)(\.[a-z\d.-]+)+(:\d+)?(\/[^\s]*)?$/i
                  })}
                  placeholder={t(
                    'Please enter a valid URL starting with http or https'
                  )}
                  sx={{
                    backgroundColor: 'var(--gray-11)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--gray-7)'
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit((data) =>
                        setSelectedFileFromUrl(data.url, handleClose)
                      )();
                    }
                  }}
                />
                <FormHelperText>
                  {errors?.url &&
                    getErrorMessage({
                      errors,
                      fieldName: 'url',
                      errorMessages: mediaLibraryErrorMessages
                    })}
                </FormHelperText>
              </FormControl>
            </div>

            <div className="mt-7 flex gap-4">
              <Button
                variant="outlined"
                startIcon={<ArrowLeftIcon />}
                onClick={handleClose}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                loading={isUploadByUrlLoading()}
                variant="contained"
              >
                {t('next')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default AddMediaContent;
